import { __assign, __read, __rest } from "tslib";
import React, { useContext, useRef } from "react";
import moment from "moment";
import { DateTable } from "./tables/DateTable";
import { CellStatus } from "./tables/BaseTable";
import { MonthTable } from "./tables/MonthTable";
import { FadeTransition } from "../transition/FadeTransition";
import { YearTable } from "./tables/YearTable";
import { QuarterTable } from "./tables/QuarterTable";
import { TimeTable } from "../timepicker/TimeTable";
import { useTranslation } from "../i18n";
import { RangePickerContext } from "../datepicker/RangePickerContext";
import { isAfter, isBefore, isSame } from "../datepicker/util";
/**
 * 获取当前时间选择范围
 */
export function getTimeRange(value, range) {
    if (range === void 0) { range = [null, null]; }
    if (!Array.isArray(range) || !value) {
        return undefined;
    }
    var _a = __read(range, 2), min = _a[0], max = _a[1];
    var timeRange = [
        moment().startOf("day"),
        moment().endOf("day"),
    ];
    if (moment.isMoment(min) && isSame(value, min, "day")) {
        timeRange[0] = min;
    }
    if (moment.isMoment(max) && isSame(value, max, "day")) {
        timeRange[1] = max;
    }
    return timeRange;
}
export function CalendarTable(_a) {
    var value = _a.value, onChange = _a.onChange, type = _a.type, onTypeChange = _a.onTypeChange, _b = _a.curViewMoment, curViewMoment = _b === void 0 ? moment() : _b, onCurViewMomentChange = _a.onCurViewMomentChange, range = _a.range, _c = _a.disabledDate, disabledDate = _c === void 0 ? function () { return true; } : _c, _d = _a.disabledMonth, disabledMonth = _d === void 0 ? function () { return true; } : _d, _e = _a.disabledQuarter, disabledQuarter = _e === void 0 ? function () { return true; } : _e, _f = _a.disabledTime, disabledTime = _f === void 0 ? function () { return ({}); } : _f, showTime = _a.showTime, rangeType = _a.rangeType, monthOnly = _a.monthOnly, hovered = _a.hovered, dateRender = _a.dateRender, props = __rest(_a, ["value", "onChange", "type", "onTypeChange", "curViewMoment", "onCurViewMomentChange", "range", "disabledDate", "disabledMonth", "disabledQuarter", "disabledTime", "showTime", "rangeType", "monthOnly", "hovered", "dateRender"]);
    var t = useTranslation();
    var rangePickerRange = useContext(RangePickerContext).range;
    // 记录 Table 切换
    var prevTypeRef = useRef(null);
    var prevType = prevTypeRef.current;
    if (type === "month" || type === "date" || type === "quarter") {
        prevTypeRef.current = type;
    }
    var tableProps = __assign(__assign({}, props), { range: range,
        onTypeChange: onTypeChange, current: curViewMoment, onCurrentChange: onCurViewMomentChange });
    var timeProps = typeof showTime === "object" ? showTime : {};
    function getValue() {
        if (!Array.isArray(value)) {
            return value;
        }
        if (rangeType === "start") {
            return value[0];
        }
        return value[1];
    }
    function getCellStatus(date, type) {
        // 单日选择
        if (!Array.isArray(value)) {
            if (moment.isMoment(value) && isSame(date, value, type)) {
                return CellStatus.Selected;
            }
            return CellStatus.Common;
        }
        // 范围选择
        if (moment.isMoment(value[0]) && isSame(date, value[0], type)) {
            // 只选中了开始或开始结束在同一天返回 Selected
            if (!moment.isMoment(value[1])) {
                return CellStatus.Selected;
            }
            if (isSame(value[0], value[1], type)) {
                return CellStatus.Selected;
            }
            return CellStatus.RangeStart;
        }
        if (moment.isMoment(value[1]) && isSame(date, value[1], type)) {
            return CellStatus.RangeEnd;
        }
        if (moment.isMoment(value[0]) && moment.isMoment(value[1])) {
            if (isBefore(value[0], date) && isAfter(value[1], date)) {
                return CellStatus.InRange;
            }
        }
        if (moment.isMoment(value[0]) &&
            !moment.isMoment(value[1]) &&
            moment.isMoment(hovered)) {
            if (isBefore(value[0], date) && isAfter(hovered, date)) {
                return CellStatus.InRangeHover;
            }
            if (isBefore(hovered, date) && isAfter(value[0], date)) {
                return CellStatus.InRangeHover;
            }
        }
        return CellStatus.Common;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(FadeTransition, { in: type === "year", exit: false },
            React.createElement(YearTable, __assign({}, tableProps, { onSelect: function (value) {
                    onCurViewMomentChange(moment(curViewMoment).year(value.year()));
                    onTypeChange(prevType || (monthOnly ? "date" : "month"));
                }, cellStatus: function (date) {
                    if (!Array.isArray(value)) {
                        if (moment.isMoment(value) && isSame(date, value, "year")) {
                            return CellStatus.Selected;
                        }
                    }
                    return CellStatus.Common;
                } }))),
        React.createElement(FadeTransition, { in: type === "quarter", exit: false },
            React.createElement(QuarterTable, __assign({}, tableProps, { onSelect: function (newValue, context) {
                    onCurViewMomentChange(moment(curViewMoment).quarter(newValue.quarter()));
                    context.type = "quarter";
                    if (!Array.isArray(value)) {
                        return onChange(newValue, context);
                    }
                    // 范围选择
                    if (moment.isMoment(value[0]) && !moment.isMoment(value[1])) {
                        if (isBefore(value[0], newValue)) {
                            return onChange([value[0], newValue], context);
                        }
                        return onChange([newValue, value[0]], context);
                    }
                    return onChange([newValue, null], context);
                }, cellStatus: function (date) { return getCellStatus(date, "quarter"); }, disabledQuarter: disabledQuarter }))),
        React.createElement(FadeTransition, { in: type === "month", exit: false },
            React.createElement(MonthTable, __assign({}, tableProps, { onSelect: function (newValue, context) {
                    onCurViewMomentChange(moment(curViewMoment).month(newValue.month()));
                    if (monthOnly) {
                        context.type = "quarter";
                        if (!Array.isArray(value)) {
                            return onChange(newValue, context);
                        }
                        // 范围选择
                        if (moment.isMoment(value[0]) && !moment.isMoment(value[1])) {
                            if (isBefore(value[0], newValue)) {
                                return onChange([value[0], newValue], context);
                            }
                            return onChange([newValue, value[0]], context);
                        }
                        return onChange([newValue, null], context);
                    }
                    return onTypeChange("date");
                }, cellStatus: function (date) { return getCellStatus(date, "month"); }, disabledMonth: disabledMonth, dateRender: dateRender }))),
        React.createElement(FadeTransition, { in: type === "date", exit: false },
            React.createElement(DateTable, __assign({}, tableProps, { value: getValue(), disabledDate: function (date) {
                    // 范围选择已选择 start，未选择 end
                    if (Array.isArray(value) &&
                        moment.isMoment(value[0]) &&
                        !moment.isMoment(value[1])) {
                        return disabledDate(date, value[0]);
                    }
                    return disabledDate(date);
                }, onSelect: function (v, context) {
                    // 单日选择
                    context.type = "date";
                    if (!Array.isArray(value)) {
                        return onChange(v, context);
                    }
                    // 范围选择
                    if (moment.isMoment(value[0]) && !moment.isMoment(value[1])) {
                        if (isBefore(value[0], v)) {
                            return onChange([value[0], v], context);
                        }
                        return onChange([v, value[0]], context);
                    }
                    return onChange([v, null], context);
                }, cellStatus: function (date) { return getCellStatus(date, "date"); }, dateRender: dateRender }))),
        React.createElement(FadeTransition, { in: type === "time", exit: false },
            React.createElement(TimeTable, __assign({ caption: moment.isMoment(getValue()) ? getValue().format("L") : t.selectTime, value: getValue(), onChange: function (v, context) {
                    context.type = "time";
                    // 日期选择完才可选择时间，所以 value 必定合法
                    if (!Array.isArray(value)) {
                        return onChange(v, context);
                    }
                    if (rangeType === "start") {
                        return onChange([v, value[1]], context);
                    }
                    return onChange([value[0], v], context);
                }, range: getTimeRange(getValue(), rangePickerRange || range) }, (disabledTime(value, rangeType) || {}), timeProps)))));
}
CalendarTable.displayName = "CalendarTable";
//# sourceMappingURL=CalendarTable.js.map